import { WSElement } from "@wingspanhq/fe-component-library";
import {
  SharedFileRequest,
  SharedFileRequestStatus
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import { useDownloadVaultFile } from "../../../../hooks/useDownloadVaultFile";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import { selectorIsRequirementReadyToUploadFile } from "../../../../selectors/selectorIsRequirementReadyToUploadFile";
import { selectorIsRequirementRejected } from "../../../../selectors/selectorIsRequirementRejected";
import { selectorIsSharedFileNotNoneContext } from "../../../../selectors/selectorIsSharedFileNotNoneContext";
import { selectorIsSharedFileReUploadAvailable } from "../../../../selectors/selectorIsSharedFileReUploadAvailable";
import { selectorIsSharedFileReUploadFlow } from "../../../../selectors/selectorIsSharedFileReUploadFlow";
import { selectorSharedFileExtractedFields } from "../../../../selectors/selectorSharedFileExtractedFields";
import { selectorSharedFileReUploadAttempts } from "../../../../selectors/selectorSharedFileReUploadAttempts";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { SharedFileDetailsWidgetsContainer } from "../../components/SharedFileDetailsWidgetsContainer";
import { SharedFileInstructionsWidgetsContainer } from "../../components/SharedFileInstructionsWidgetsContainer";
import { useSubmitSharedFileModal } from "../../components/useSubmitSharedFileModal";
import { useUploadFileModal } from "../../components/useUploadFileModal";
import { getSharedFilePayeeRequirementStatus } from "../helpers/getSharedFilePayeeRequirementStatus";
import { getSharedFileProgressSteps } from "../helpers/getSharedFileProgressSteps";
import { getSharedFileRequirementTitle } from "../helpers/getSharedFileRequirementTitle";
import { mapContextTypeExtractedFieldsToDataView } from "../helpers/mapContextTypeExtractedFieldsToDataView";
import { renderSharedFileName } from "../helpers/renderSharedFileName";
import {
  MAX_SHARED_FILE_UPLOAD_ATTEMPTS,
  SHARED_FILE_REQUIREMENT_EVENT
} from "../utils";

import { payerSharedFileRenderCardDescription } from "./payerSharedFileRenderCardDescription";

export const usePayerSharedFileRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  SharedFileRequest | undefined
> => {
  const downloadVaultFile = useDownloadVaultFile();
  const uploadFileModal = useUploadFileModal();
  const submitWithIssuesModal = useSubmitSharedFileModal();

  const handleSubmitAnyway = async (
    requirement: RequirementTableItemData,
    sharedFileRequest: SharedFileRequest,
    context: ContextItem,
    onSuccess?: () => void
  ) => {
    await submitWithIssuesModal.open({
      sharedFileRequestId: sharedFileRequest.id!,
      onSubmit: () => {
        onSuccess?.();
        callback(SHARED_FILE_REQUIREMENT_EVENT.SubmitFileAnyway, requirement);
      }
    });
  };

  // The name/type and the bytes are two separate vault reads: the metadata read
  // is cache-first, and the content read is typed `void` by the SDK (the
  // endpoint answers a redirect), so the blob comes off the raw response — the
  // same escape hatch the payStatement PDF download uses.
  const handleRequirementDownload = async (
    sharedFileRequest: SharedFileRequest,
    requirement: RequirementTableItemData
  ) => {
    if (await downloadVaultFile(sharedFileRequest.fileId!)) {
      callback(SHARED_FILE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
    }
  };

  const handleUploadFile = async (
    requirement: RequirementTableItemData,
    sharedFileRequest: SharedFileRequest,
    context: ContextItem
  ) => {
    await uploadFileModal.open({
      sharedFileRequest,
      onSuccess: () => {
        callback(SHARED_FILE_REQUIREMENT_EVENT.UploadFile, requirement);
      }
    });
  };

  const getUploadFileAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    sharedFileRequest: SharedFileRequest | undefined
  ) => {
    if (
      selectorIsRequirementReadyToUploadFile(requirement, sharedFileRequest)
    ) {
      return async () => {
        await handleUploadFile(requirement, sharedFileRequest!, context);
      };
    }

    return undefined;
  };

  const getDownloadFileAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    sharedFileRequest: SharedFileRequest | undefined
  ) => {
    const isDocumentReadyToDownload = selectorIsRequirementReadyToDownloadFile(
      requirement,
      sharedFileRequest
    );

    if (isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(sharedFileRequest!, requirement);
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, sharedFileRequest) => {
      return !selectorIsSharedFileReUploadFlow(sharedFileRequest)
        ? selectorIsRequirementReadyToUploadFile(requirement, sharedFileRequest)
        : selectorIsSharedFileReUploadAvailable(sharedFileRequest);
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      sharedFileRequest
    ) => {
      return sharedFileRequest?.status
        ? getSharedFileProgressSteps(
            sharedFileRequest.status,
            sharedFileRequest.contextType
          )
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, sharedFileRequest) => {
      return sharedFileRequest?.id ? (
        <SharedFileDetailsWidgetsContainer
          contextType={RequirementContextType.Payer}
          requirement={requirement}
          sharedFileRequestId={sharedFileRequest.id}
          onSuccess={callback}
        />
      ) : null;
    },
    requirementInstructionsWidgets: (
      requirement,
      context,
      sharedFileRequest
    ) => {
      return sharedFileRequest ? (
        <SharedFileInstructionsWidgetsContainer
          contextType={RequirementContextType.Payer}
          sharedFileRequest={sharedFileRequest}
        />
      ) : null;
    },
    requirementInstructions: () => {
      return instructions => {
        return <RequirementInstructionsView instructions={instructions} />;
      };
    },
    requirementName: getSharedFileRequirementTitle,
    requirementStatusAlert: (requirement, context, sharedFileRequest) => {
      if (selectorIsRequirementRejected(requirement)) {
        return {
          theme: "error",
          title: "Rejected",
          children:
            "The client rejected the submission and the requirement is marked incomplete.",
          icon: "alert-circle"
        };
      }

      if (
        selectorIsSharedFileNotNoneContext(sharedFileRequest?.contextType) &&
        sharedFileRequest?.status === SharedFileRequestStatus.PENDING_SUBMISSION
      ) {
        if (!selectorIsSharedFileReUploadAvailable(sharedFileRequest)) {
          return {
            theme: "warning",
            title: "Action required",
            children: (
              <>
                The uploaded file failed to pass all validations and you’ve
                reached the maximum upload limit. You have the following
                options:
                <WSElement as="ul" ml="L">
                  <li>
                    Submit the invalidated file anyway for review by client
                  </li>
                  <li>
                    Contact the client to lift the limit and re-request an
                    upload
                  </li>
                </WSElement>
              </>
            ),
            icon: "alert",
            action: {
              text: "Submit for review",
              onClick: () => {
                handleSubmitAnyway(requirement, sharedFileRequest!, context);
              }
            }
          };
        }

        const attempts =
          MAX_SHARED_FILE_UPLOAD_ATTEMPTS -
          selectorSharedFileReUploadAttempts(sharedFileRequest);

        return {
          theme: "warning",
          title: "Action required",
          children: (
            <>
              The uploaded file failed to pass all validations. You have the
              following options:
              <WSElement as="ul" ml="L">
                <li>
                  Re-upload a valid document to try again. You have {attempts}{" "}
                  attempts remaining before the requirement is marked as failed
                  to be further reviewed by the client.
                </li>
                <li>Submit the invalidated file anyway for review by client</li>
              </WSElement>
            </>
          ),
          icon: "alert",
          action: {
            text: "Upload new file",
            onClick: () => {
              handleUploadFile(requirement, sharedFileRequest!, context);
            }
          }
        };
      }

      if (sharedFileRequest?.status === SharedFileRequestStatus.FAILED) {
        return {
          theme: "error",
          title: "Failed",
          children:
            "The uploaded file was submitted without passing all validations.",
          icon: "alert"
        };
      }

      return {
        theme: "warning",
        title: "Action required",
        children:
          "Complete the requirement in order to be eligible to receive payments from your client.",
        icon: "alert"
      };
    },
    requirementDetails: (requirement, context, sharedFileRequest) => {
      const uploadAction = getUploadFileAction(
        requirement,
        context,
        sharedFileRequest
      );
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFileRequest
      );
      const extractedFields =
        selectorSharedFileExtractedFields(sharedFileRequest);

      return {
        header: {
          label: {
            text: renderSharedFileName(
              requirement,
              context,
              sharedFileRequest
            ) as string
          }
        },
        data: extractedFields
          ? mapContextTypeExtractedFieldsToDataView(
              extractedFields,
              sharedFileRequest?.contextType
            )
          : [],
        children: payerSharedFileRenderCardDescription(
          requirement,
          sharedFileRequest
        ),
        actions: {
          alignment: "fill",
          buttons: [
            {
              label: "Download file",
              onAsyncClick: downloadAction,
              kind: "Secondary",
              visible: !!downloadAction
            },
            {
              label: "Upload file",
              onAsyncClick: uploadAction,
              kind: "Primary",
              visible: !!uploadAction
            },
            {
              label: "Upload new file",
              onAsyncClick: async () => {
                await handleUploadFile(
                  requirement,
                  sharedFileRequest!,
                  context
                );
              },
              kind: "Primary",
              disabled:
                !selectorIsSharedFileReUploadAvailable(sharedFileRequest),
              visible: selectorIsSharedFileReUploadFlow(sharedFileRequest)
            }
          ]
        }
      };
    },
    toolbarActions: (requirement, context, sharedFileRequest) => {
      const actions: RequirementAction[] = [];

      const uploadFileAction = getUploadFileAction(
        requirement,
        context,
        sharedFileRequest
      );
      const downloadFileAction = getDownloadFileAction(
        requirement,
        context,
        sharedFileRequest
      );

      if (
        sharedFileRequest &&
        selectorIsSharedFileReUploadFlow(sharedFileRequest)
      ) {
        actions.push({
          label: "Submit anyway",
          onAsyncClick: async () => {
            await handleSubmitAnyway(requirement, sharedFileRequest, context);
          },
          buttonKind: "Tertiary",
          icon: "send",
          buttonIcon: null,
          disabled:
            sharedFileRequest?.status !==
            SharedFileRequestStatus.PENDING_SUBMISSION,
          mainAction: true
        });

        actions.push({
          label: "Upload new file",
          onAsyncClick: async () => {
            await handleUploadFile(requirement, sharedFileRequest!, context);
          },
          buttonKind: "Primary",
          disabled: !selectorIsSharedFileReUploadAvailable(sharedFileRequest),
          icon: "file",
          buttonIcon: null,
          mainAction: true
        });
      } else {
        if (uploadFileAction) {
          actions.push({
            onAsyncClick: uploadFileAction,
            label: "Upload file",
            buttonKind: "Primary",
            icon: "file",
            buttonIcon: null,
            mainAction: true
          });
        }
      }

      if (downloadFileAction) {
        actions.push({
          onAsyncClick: downloadFileAction,
          label: "Download file",
          buttonKind:
            sharedFileRequest?.status ===
            SharedFileRequestStatus.PENDING_SUBMISSION
              ? undefined
              : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, sharedFileRequest) => {
      return {
        text: getSharedFileRequirementTitle(
          requirement,
          context,
          sharedFileRequest
        )
      };
    },
    typeCell: (requirement, _, sharedFileRequest) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type,
        contextType: sharedFileRequest?.contextType
      }),
    statusCell: (requirement, context, sharedFileRequest) => {
      return getSharedFilePayeeRequirementStatus(
        requirement,
        context,
        sharedFileRequest
      );
    }
  };
};
